import { commissionCopy, commissionPercent } from "../../../../supabase/functions/_shared/clinicianSettlementPolicy.mjs";
import { formatDoctorPaymentMethod } from "@/services/doctorPaymentPreferences.js";
import React from "react";
import { useNavigate } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import PreferencesPageShell from "@/features/settings/preferences/PreferencesPageShell.jsx";
import { loadDoctorBillingReadiness } from "@/services/doctorBillingProfileService.js";
import { getMarketPaymentCoverage } from "@/services/marketPaymentService.js";

function MetricCard({
  label,
  value,
  hint = "",
  valueClassName = "text-title-small text-on-surface sm:text-title-medium",
}) {
  return (
    <Card as="section" className="p-4">
      <div className="text-label-large text-on-surface-var">{label}</div>
      <div className={["mt-2", valueClassName].join(" ")}>{value}</div>
      {hint ? <div className="mt-2 text-body-small text-on-surface-var">{hint}</div> : null}
    </Card>
  );
}

function StatusPill({ label, tone = "neutral" }) {
  const toneClassName =
    tone === "success"
      ? "bg-[var(--state-primary-12)] text-primary"
      : tone === "warning"
      ? "bg-[rgba(var(--clr-on-surface),0.08)] text-on-surface"
      : "bg-[rgba(var(--clr-on-surface),0.08)] text-on-surface-var";

  return (
    <span
      className={[
        "inline-flex items-center rounded-full px-3 py-1 text-label-medium",
        toneClassName,
      ].join(" ")}
    >
      {label}
    </span>
  );
}

function ActionButton({ label, onClick, tone = "primary", className = "" }) {
  const variant =
    tone === "secondary" ? "tonal" : tone === "ghost" ? "ghost" : "default";

  return (
    <Button
      type="button"
      onClick={onClick}
      size="default"
      variant={variant}
      className={className}
    >
      {label}
    </Button>
  );
}

function formatCount(value) {
  const count = Number(value);
  return Number.isFinite(count) ? String(count) : "—";
}

export default function PlansSubscriptionsPage({ role = "patient" }) {
  const nav = useNavigate();
  const { t, languageCode } = useLanguage();
  const auth = useAuth() || {};
  const userId = String(auth?.user?.id || "").trim();
  const [loading, setLoading] = React.useState(role === "doctor");
  const [error, setError] = React.useState("");
  const [doctorBillingState, setDoctorBillingState] = React.useState({
    servicesCount: 0,
    consultanciesCount: 0,
    paymentMethodCount: 0,
    paymentMethodLabels: [],
    signatureReady: false,
    payoutReady: false,
    practiceLogoReady: false,
    agreementReady: false,
    agreementSignedAt: "",
    agreementVersion: "",
  });
  const marketCoverage = React.useMemo(() => getMarketPaymentCoverage(), []);

  const refreshDoctorBillingState = React.useCallback(async () => {
    if (role !== "doctor" || !userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const nextState = await loadDoctorBillingReadiness(userId);
      setDoctorBillingState(nextState);
    } catch (nextError) {
      setError(
        t(
          "settings.preferences.plans.loadError",
          "Unable to load billing readiness right now."
        )
      );
    } finally {
      setLoading(false);
    }
  }, [role, t, userId]);

  React.useEffect(() => {
    let alive = true;

    async function loadDoctorBillingState() {
      try {
        if (role !== "doctor" || !userId) {
          if (alive) setLoading(false);
          return;
        }
        const nextState = await loadDoctorBillingReadiness(userId);
        if (!alive) return;
        setDoctorBillingState(nextState);
      } catch (nextError) {
        if (!alive) return;
        setError(
          t(
            "settings.preferences.plans.loadError",
            "Unable to load billing readiness right now."
          )
        );
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadDoctorBillingState();
    return () => {
      alive = false;
    };
  }, [role, t, userId]);

  if (role === "doctor") {
    const stripeSummary = doctorBillingState.payoutReady
      ? t("settings.preferences.plans.doctor.stripeConnected", "Connected")
      : t("settings.preferences.plans.doctor.stripePending", "Not connected yet");
    const paymentMethodsSummary =
      doctorBillingState.paymentMethodsUnavailable
        ? t("settings.payoutPreferences.capabilitiesError", "Payment methods could not be checked. Try again.")
        : doctorBillingState.paymentMethodCount > 0
        ? (doctorBillingState.paymentMethods || []).map(option => formatDoctorPaymentMethod(option, t)).join(", ")
        : t(
            "settings.preferences.plans.doctor.paymentMethodsPending",
            "Add payout and payment methods to complete billing readiness."
          );
    const agreementSummary = doctorBillingState.agreementReady
      ? doctorBillingState.agreementVersion
        ? t(
            "settings.preferences.plans.doctor.agreementSignedVersion",
            "Signed ({version})",
            { version: doctorBillingState.agreementVersion }
          )
        : t("settings.preferences.plans.doctor.agreementSigned", "Signed")
      : t("settings.preferences.plans.doctor.agreementPending", "Pending");
    const brandSummary = doctorBillingState.practiceLogoReady
      ? t("settings.preferences.plans.doctor.logoReady", "Practice logo saved")
      : t("settings.preferences.plans.doctor.logoPending", "Practice logo not added yet");

    return (
      <PreferencesPageShell
        role={role}
        title={t("settings.preferences.plans.title", "Plans & Subscriptions")}
        subtitle={t(
          "settings.preferences.plans.doctor.subtitleLong",
          "Manage the direct-care billing lane: AfriMed™ commission, payout readiness, services, and the trust signals that support paid bookings."
        )}
      >
        <Card as="section" className="p-4">
          <div className="max-w-[42rem]">
            <div className="text-label-large text-on-surface-var">
              {t("settings.preferences.plans.doctor.businessModel", "Direct-care billing model")}
            </div>
            <div className="mt-1 text-title-medium text-on-surface sm:text-title-large">
              {t(
                "settings.preferences.plans.doctor.commissionTitle",
                commissionCopy.en
              )}
            </div>
            <div className="mt-2 text-body-medium text-on-surface-var">
              {t(
                "settings.preferences.plans.doctor.commissionBody",
                "Capture follows completed service. Bank credit follows external provider settlement timelines."
              )}
            </div>
          </div>
        </Card>

        <div className="flex flex-col gap-2 sm:flex-row">
          <ActionButton
            label={t("settings.preferences.plans.doctor.servicesCta", "Services & pricing")}
            onClick={() => nav("/settings/doctor/p/services")}
            className="w-full sm:w-auto"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <MetricCard
            label={t("settings.preferences.plans.doctor.servicesReady", "Active services")}
            value={loading ? "…" : formatCount(doctorBillingState.servicesCount)}
            hint={t(
              "settings.preferences.plans.doctor.servicesHint",
              "Patients can only book what you have priced and published."
            )}
          />
          <MetricCard
            label={t("settings.preferences.plans.doctor.locationsReady", "Consult locations")}
            value={loading ? "…" : formatCount(doctorBillingState.consultanciesCount)}
            hint={t(
              "settings.preferences.plans.doctor.locationsHint",
              "Consultancy settings still own where in-person bookings happen."
            )}
          />
          <MetricCard
            label={t("settings.preferences.plans.doctor.paymentsReady", "Payment methods")}
            value={loading || doctorBillingState.paymentMethodsUnavailable ? "…" : formatCount(doctorBillingState.paymentMethodCount)}
            hint={paymentMethodsSummary}
          />
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <MetricCard
            label={t("settings.preferences.plans.doctor.stripeStatus", "Payments")}
            value={loading ? "…" : stripeSummary}
            hint={t(
              "settings.preferences.plans.doctor.stripeStatusHint",
              "Your payment setup is ready for AfriMed™ paid consultations."
            )}
          />
          <MetricCard label={t("settlement.commission", "AfriMed commission")} value={`${commissionPercent}%`} hint={t("settlement.commissionCopy", commissionCopy.en)} />
          <MetricCard
            label={t("settings.preferences.plans.doctor.agreementStatus", "Agreement")}
            value={loading ? "…" : agreementSummary}
            hint={t(
              "settings.preferences.plans.doctor.agreementStatusHint",
              "Agreement acceptance should be signed and versioned before live payouts are enabled."
            )}
          />
        </div>

        <Card as="section" className="p-4">
          <div className="max-w-[40rem]">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-title-small text-on-surface">
                {t("settings.preferences.plans.doctor.stripeTitle", "Payout readiness")}
              </div>
              <StatusPill
                label={stripeSummary}
                tone={doctorBillingState.payoutReady ? "success" : "warning"}
              />
            </div>
            <div className="mt-2 text-body-medium text-on-surface-var">
              {t(
                "settings.preferences.plans.doctor.stripeBody",
                "Keep your services, payout preferences, agreement details and prescriber signature up to date so payments can be processed smoothly."
              )}
            </div>
          </div>
        </Card>

        <div className="flex flex-col gap-2 sm:flex-row">
          <ActionButton
            label={
              doctorBillingState.payoutReady
                ? t("settings.preferences.plans.doctor.connectStripeManage", "Manage payments")
                : t("settings.preferences.plans.doctor.connectStripeCta", "Set up payments")
            }
            onClick={() => nav("/settings/doctor/p/payout-preferences")}
            tone={doctorBillingState.payoutReady ? "secondary" : "primary"}
            className="w-full sm:w-auto"
          />
          <ActionButton
            label={t("settings.preferences.plans.doctor.payoutCta", "Payout preferences")}
            onClick={() => nav("/settings/doctor/p/payout-preferences")}
            tone="ghost"
            className="w-full sm:w-auto"
          />
        </div>

        <Card as="section" className="p-4"><p>{t("settlement.commissionCopy", commissionCopy.en)}</p></Card>

        <section className="py-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-title-small text-on-surface">
              {t(
                "settings.preferences.plans.doctor.diasporaTitle",
                "SADC + diaspora sponsor payments"
              )}
            </div>
            <StatusPill
              label={t(
                "settings.preferences.plans.doctor.diasporaCoverage",
                "{count} markets",
                { count: marketCoverage.totalCount }
              )}
              tone="neutral"
            />
          </div>
          <div className="mt-2 text-body-medium text-on-surface-var">
            {t(
              "settings.preferences.plans.doctor.diasporaBody",
              "Patients can ask family members, guardians or sponsors locally or abroad to fund their care through a secure AfriMed™ payment request."
            )}
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <MetricCard
              label={t("settings.preferences.plans.doctor.diasporaSadc", "SADC markets")}
              value={String(marketCoverage.sadcCount)}
              hint={t(
                "settings.preferences.plans.doctor.diasporaSadcHint",
                "Local patients and local sponsors across the region."
              )}
            />
            <MetricCard
              label={t("settings.preferences.plans.doctor.diasporaDiaspora", "Diaspora markets")}
              value={String(marketCoverage.diasporaCount)}
              hint={t(
                "settings.preferences.plans.doctor.diasporaDiasporaHint",
                "UK, US, Gulf, Europe, Australia, and other diaspora funding lanes."
              )}
            />
          </div>
        </section>

        <Card as="section" className="p-4">
          <div className="text-title-small text-on-surface">
            {t(
              "settings.preferences.plans.doctor.fxTitle",
              "International sponsor payments"
            )}
          </div>
          <div className="mt-2 text-body-medium text-on-surface-var">
            {t(
              "settings.preferences.plans.doctor.fxBody",
              "AfriMed™ processes the consultation in the service payment currency. When a sponsor uses a different home currency, their bank or payment provider manages the conversion and any related fees."
            )}
          </div>
        </Card>

        <section className="py-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-title-small text-on-surface">
              {t("settings.preferences.plans.doctor.brandingTitle", "Trust, branding, and documentation")}
            </div>
            <StatusPill
              label={brandSummary}
              tone={doctorBillingState.practiceLogoReady ? "success" : "neutral"}
            />
          </div>
          <div className="mt-2 text-body-medium text-on-surface-var">
            {t(
              "settings.preferences.plans.doctor.brandingBody",
              "Your saved signature is already reusable for prescriptions. Practice logo uploads and private signed-agreement capture now live here so invoices, receipts, and prescription documents can inherit one verified doctor brand source."
            )}
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <MetricCard
              label={t("settings.preferences.plans.doctor.brandLogo", "Practice logo")}
              value={
                loading
                  ? "…"
                  : doctorBillingState.practiceLogoReady
                  ? t("settings.preferences.plans.doctor.brandLogoReady", "Ready")
                  : t("settings.preferences.plans.doctor.brandLogoMissing", "Missing")
              }
              hint={t(
                "settings.preferences.plans.doctor.brandLogoHint",
                "Use a dedicated logo source for invoices, receipts, and future branded documents."
              )}
            />
            <MetricCard
              label={t("settings.preferences.plans.doctor.brandSignature", "Prescriber signature")}
              value={
                loading
                  ? "…"
                  : doctorBillingState.signatureReady
                  ? t("settings.preferences.plans.doctor.brandSignatureReady", "Ready")
                  : t("settings.preferences.plans.doctor.brandSignatureMissing", "Missing")
              }
              hint={t(
                "settings.preferences.plans.doctor.brandSignatureHint",
                "Prescription signing already reuses this asset."
              )}
            />
            <MetricCard
              label={t("settings.preferences.plans.doctor.brandAgreement", "Signed agreement")}
              value={loading ? "…" : agreementSummary}
              hint={doctorBillingState.agreementReady
                ? [t("settings.preferences.plans.doctor.signedOn", "Signed at {date}", { date: new Intl.DateTimeFormat(languageCode === "fr" ? "fr" : "en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(doctorBillingState.agreementSignedAt)) }), !doctorBillingState.agreementCurrent ? t("settings.preferences.plans.doctor.agreementUpdate", "A newer agreement version is available; your signed archive is preserved.") : ""].filter(Boolean).join(". ")
                : t("settings.preferences.plans.doctor.brandAgreementHint", "Signed agreements should be stored privately and versioned.") }
            />
          </div>

        </section>

        <div className="flex flex-col gap-2 sm:flex-row">
          <ActionButton
            label={t(
              "settings.preferences.plans.doctor.brandingPanelCta",
              "Branding & agreement"
            )}
            onClick={() => nav("/settings/doctor/preferences/plans/branding")}
            className="w-full sm:w-auto"
          />
        </div>

        {doctorBillingState.paymentMethodsUnavailable ? <ActionButton label={t("common.retry", "Try again")} onClick={refreshDoctorBillingState} /> : null}
        {error ? (
          <p className="text-body-medium text-[rgb(var(--schemes-critical))]">{error}</p>
        ) : null}
      </PreferencesPageShell>
    );
  }

  return (
    <PreferencesPageShell
      role={role}
        title={t("settings.preferences.plans.title", "Plans & Subscriptions")}
        subtitle={t(
          "settings.preferences.plans.patient.subtitleLong",
          "Patients do not need a paid subscription to use AfriMed™. This surface keeps the account model clear while receipts and payment history stay nearby."
        )}
      >
      <Card as="section" className="p-4">
            <div className="text-label-large text-on-surface-var">
              {t("settings.preferences.plans.patient.accountType", "Account type")}
            </div>
            <div className="mt-1 text-title-medium text-on-surface sm:text-title-large">
              {t("settings.preferences.plans.patient.freeTitle", "Free patient account")}
            </div>
        <div className="mt-2 text-body-medium text-on-surface-var">
          {t(
            "settings.preferences.plans.patient.freeBody",
            "Patients are not charged a membership fee on the direct-care lane. Charges appear only when you book paid services with a doctor on AfriMed™."
          )}
        </div>
      </Card>

      <div className="grid gap-3 md:grid-cols-2">
        <MetricCard
          label={t("settings.preferences.plans.patient.transactions", "Transactions")}
          value={t("settings.preferences.plans.patient.transactionsValue", "See receipts")}
          valueClassName="text-title-medium text-on-surface sm:text-title-large"
          hint={t(
            "settings.preferences.plans.patient.transactionsHint",
            "Open Transactions & invoices for payment history and receipt exports."
          )}
        />
        <MetricCard
          label={t("settings.preferences.plans.patient.downloads", "Downloads")}
          value={t("settings.preferences.plans.patient.downloadsValue", "Exports ready")}
          valueClassName="text-title-medium text-on-surface sm:text-title-large"
          hint={t(
            "settings.preferences.plans.patient.downloadsHint",
            "Quick receipt and billing exports are already staged in Downloads while fuller archive tools grow."
          )}
        />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <ActionButton
          label={t("settings.preferences.plans.patient.transactionsCta", "Transactions & invoices")}
          onClick={() => nav("/settings/patient/preferences/transactions")}
          className="w-full sm:w-auto"
        />
        <ActionButton
          label={t("settings.preferences.plans.patient.downloadsCta", "Downloads")}
          onClick={() => nav("/settings/patient/preferences/downloads")}
          tone="secondary"
          className="w-full sm:w-auto"
        />
      </div>
    </PreferencesPageShell>
  );
}
